import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { extractTreeByName, getNodeName } from 'base/webflow/data_attributes';
import {
  isReplaceableTextNode,
  textContentsOf,
} from 'base/webflow/replaceable_content';
import { NestedNode, NestedTagNode } from 'base/webflow/schema';
import { styleGuideStyleById } from 'base/webflow/style_guide';
import { ElementInstance, ElementInstanceState } from '../model';

export type ReplaceableTextContent2<EI> = {
  element: EI;
  nodeId: string;
  original: string;
  /* A summary of class names applied to this node, for usage in a LLM prompt */
  cssClassesForAi: string;
};

export function findElementReplacableTextContent<
  EI extends ElementInstance | ElementInstanceState,
>(element: EI, library: LibraryCache): ReplaceableTextContent2<EI>[] {
  const component = library.getCachedPublicComponentJson(
    element.source.slug,
  )?.value;
  if (!component) return [];
  const root = extractTreeByName(component, element.source.rootName);

  const localClassIdToName = new Map(
    component.payload.styles.map((s) => [s._id, s.name]),
  );
  const parentMap = createParentByIdMap(root);

  function getHTagOrClasses(node: NestedTagNode): string | undefined {
    if (node.tag.startsWith('h')) {
      return node.tag;
    }
    const classes = node.classes
      .map(
        (id) => localClassIdToName.get(id) || styleGuideStyleById.get(id)?.name,
      )
      .filter(
        (name) =>
          name &&
          // remove stylistic classes (context is intended for a copy-writing ai)
          !name.startsWith('margin-') &&
          !name.startsWith('text-color-'),
      )
      .join(' ');
    if (classes) {
      return classes;
    }
  }

  const output: ReplaceableTextContent2<EI>[] = [];
  function walk(node: NestedNode) {
    if (node.text) return;
    const nodeName = getNodeName(node);
    const slotOverride = nodeName && element.slotOverrides.get(nodeName);
    if (slotOverride) {
      for (const item of slotOverride.items) {
        // why is this case needed?
        output.push(
          ...findElementReplacableTextContent<EI>(item as EI, library),
        );
      }
    } else if (isReplaceableTextNode(node)) {
      const myContext = getHTagOrClasses(node);
      const parent = parentMap.get(node._id);
      const parentContext = parent ? getHTagOrClasses(parent) : undefined;
      const grandparent = parent ? parentMap.get(parent._id) : undefined;
      const grandparentContext = grandparent
        ? getHTagOrClasses(grandparent)
        : undefined;
      const cssClassesForAi = [myContext, parentContext, grandparentContext]
        .filter(exists)
        .slice(0, 2) // parents intended as a fallback, don't use multiple levels of parent
        .join(' ');
      output.push({
        element,
        nodeId: node._id,
        cssClassesForAi,
        original: textContentsOf(node),
      });
    } else {
      node.children.forEach(walk);
    }
  }
  walk(root);
  return output;
}

function createParentByIdMap(root: NestedNode): Map<string, NestedTagNode> {
  const out = new Map<string, NestedTagNode>();
  function visit(node: NestedNode) {
    if (node.text) return;
    for (const child of node.children) {
      out.set(child._id, node);
      visit(child);
    }
  }
  visit(root);
  return out;
}
